// Lo que dice una tarjeta de la cola del entrenador — GEMELO en los dos repos
// (`scripts/guard-gemelos-catalogo.mjs`).
//
// Por qué es gemelo y no dos funciones parecidas: la cola sale de UNA RPC
// (`formal_evaluations_pending_for_trainer`) y la pintan CUATRO superficies —dos
// de la app y dos del panel—. Si el texto se escribe cuatro veces, se separa; y
// esta serie va justo de eso: la mig 288 metió `source` en la respuesta y
// **ninguna de las cuatro lo leía**, así que una asignación de dirección se
// presentaba como «0 sesiones desde —».
//
// Y ese «0 sesiones desde —» no era un caso raro: dirección asigna precisamente
// cuando el alumno NO llega al umbral (para eso existe la tabla de seguimiento
// de la 194), así que en una asignación el contador vale 0 y no hay informe
// anterior. La tarjeta que más información debería dar era la más vacía.
//
// El nombre y la fecha vienen de la mig 311. No se pueden calcular aquí: quien
// asignó es `formal_evaluations.created_by` y **el entrenador no puede leer ese
// perfil** (no ve el de un admin ni el de un director), así que lo resuelve la
// RPC, que es SECURITY DEFINER.

//
// ⚠️ Este tipo se escribe A MANO y no se saca de `database.types.ts`. El
// generador (postgres-meta) **no expresa la nullability de un RETURNS TABLE**:
// allí `assigned_by`, `assigned_at` y `last_eval_date` salen como `string` a
// secas, aunque las tres sean NULL en la vía del umbral. Poner `| null` en el
// fichero generado no vale —el gate de tipos compara con lo que genera la
// herramienta y se pone rojo—, así que la verdad vive aquí.
/** Una fila de la cola. Los cuatro clientes leen de aquí. */
export interface PendienteDeEvaluar {
  sessions_since_last: number;
  last_eval_date: string | null;
  /** mig 288: 'asignado' | 'umbral'. Opcional: un cliente viejo no lo trae. */
  source?: string | null;
  /** mig 311: nombre de quien la asignó. NULL en las de umbral. */
  assigned_by?: string | null;
  /** mig 311: cuándo se asignó (created_at del borrador). NULL en las de umbral. */
  assigned_at?: string | null;
}

/**
 * Una asignación de dirección, frente a un alumno que ha llegado al umbral.
 *
 * Se pregunta por `source`, no por «¿tiene assigned_by?»: la columna que decide
 * es la de la 288 y el nombre es un adorno que puede faltar (una cuenta
 * borrada). Deducirlo del adorno haría que una asignación sin autor se
 * presentara como si el alumno hubiera llegado al umbral — y con 0 sesiones.
 */
export function esAsignada(p: PendienteDeEvaluar): boolean {
  return p.source === 'asignado';
}

/** Cuántas de la cola te ha puesto dirección. Para los contadores. */
export function cuantasAsignadas(lista: PendienteDeEvaluar[]): number {
  return lista.filter(esAsignada).length;
}

// Las fechas de `date` se leen a mediodía para que no se vayan un día por la
// zona horaria; `assigned_at` ya es un instante con zona.
function fecha(d: string | null | undefined, conHora = false): string | null {
  if (!d) return null;
  const f = new Date(conHora ? d : `${d}T00:00:00`);
  if (Number.isNaN(f.getTime())) return null;
  return f.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * El subtítulo de la tarjeta. Una sola frase, y distinta según de dónde venga:
 *
 *   asignada → «Te la asignó Jose Milla · 17 sept 2026»
 *   umbral   → «8 sesiones desde 01 may 2026»
 *
 * En una asignación sin fecha legible se dice quién, sin inventar cuándo. Y si
 * tampoco hay nombre —cuenta borrada, o un INSERT a mano sin autor— la RPC ya
 * devuelve 'Dirección', así que aquí el respaldo es el mismo y nunca sale un
 * hueco.
 */
export function motivoDelPendiente(p: PendienteDeEvaluar): string {
  if (esAsignada(p)) {
    const quien = p.assigned_by?.trim() || 'Dirección';
    const cuando = fecha(p.assigned_at, true);
    return cuando ? `Te la asignó ${quien} · ${cuando}` : `Te la asignó ${quien}`;
  }
  // La vía del umbral, con el plural arreglado de paso: decía «1 sesiones».
  const n = p.sessions_since_last;
  const sesiones = `${n} ${n === 1 ? 'sesión' : 'sesiones'}`;
  const desde = fecha(p.last_eval_date);
  // Sin informe anterior el contador va desde el principio. Antes esto era
  // «desde —», que no dice nada.
  return desde ? `${sesiones} desde ${desde}` : `${sesiones} · sin informe anterior`;
}

// ── EL TITULAR Y EL DESGLOSE DE LOS CONTADORES (pieza B) ────────────────────
//
// Las dos superficies de inicio —`MonitorDashboardPage` en el panel y
// `MonitorHomeScreen` en la app— tenían el mismo par de defectos, y los dos son
// de lo que el número DICE, no de lo que vale:
//
//   1 · el titular era «3 alumnos sin evaluar este mes», que es el lenguaje de
//       la vía del umbral, puesto encima de un total que incluye encargos de
//       dirección. Con 2 asignadas de 3, el titular afirmaba lo que no era:
//       nadie «llegó al umbral», se lo mandaron;
//   2 · el desglose solo salía cuando había asignadas (`asignadas > 0 && …`), y
//       una línea que no se pinta no dice «ninguna»: dice nada. Es el mismo
//       error que separar un 0 contado de un `null` sin contar, una capa más
//       arriba. Ahora los dos términos se escriben SIEMPRE, con sus ceros.
//
// Vive aquí y no en cada pantalla por lo mismo que el resto del fichero: son
// CUATRO superficies y el texto se separa en cuanto se escribe dos veces.

/**
 * El titular de la tarjeta de evaluaciones pendientes.
 *
 * Con algo asignado, el titular ES la asignación: es lo que tiene un remitente
 * y una fecha, y lo que alguien está esperando. El resto de la cola se cuenta
 * en el desglose, que va debajo y no se calla ningún término.
 */
export function titularDeLaCola(total: number, asignadas: number): string {
  if (asignadas > 0) {
    return asignadas === 1
      ? 'Dirección te ha asignado 1 informe'
      : `Dirección te ha asignado ${asignadas} informes`;
  }
  return total === 1 ? '1 alumno sin evaluar este mes' : `${total} alumnos sin evaluar este mes`;
}

/**
 * El desglose por procedencia, para que el número de arriba no valga por dos
 * cosas distintas a la vez.
 *
 * SIEMPRE los dos términos, el cero incluido: «ninguna asignada · 3 por
 * sesiones acumuladas» responde la pregunta, y la línea ausente de antes la
 * dejaba sin responder. Devuelve `null` solo cuando no hay cola —ni contada ni
 * contable—, que es cuando no hay nada que desglosar.
 */
export function desgloseDeLaCola(total: number | null, asignadas: number): string | null {
  if (total === null || total <= 0) return null;
  const resto = Math.max(0, total - asignadas);
  const izq =
    asignadas === 0
      ? 'ninguna asignada'
      : asignadas === 1
        ? '1 asignada'
        : `${asignadas} asignadas`;
  const der = resto === 0 ? 'ninguna por sesiones acumuladas' : `${resto} por sesiones acumuladas`;
  return `${izq} · ${der}`;
}
